import React, { useMemo, useCallback } from 'react';
import { Line } from 'react-chartjs-2';

// Define necessary constants (These should be imported/defined globally in your project)
const STATUSES = ['Assigned', 'Started', 'InReview', 'Completed'];
const STATUS_COLORS = {
    'Completed': '#2ECC71',
    'InReview': '#F39C12',
    'Started': '#3498DB',
    'Assigned': '#13d2dfff',
};
const COLORS = [
    '#4C78A8', '#F58518', '#E45756', '#72B7B2', '#54A24B',
    '#EECA3B', '#B279A2', '#FF9DA7', '#9D755D', '#BAB0AC'
];
export const StudentTrendLineChart = ({ data, students }) => {

    const studentColors = useMemo(() => {
        return students?.reduce((acc, s, i) => ({
            ...acc,
            [s.name]: COLORS[i % COLORS.length]
        }), {});
    }, [students]);

    // 🎯 FIX: Ensuring the keys returned by useMemo match the destructured names (chartData, options)
    const { chartData, options } = useMemo(() => {

        const extractDate = (dateTimeString) => {
            return dateTimeString ? String(dateTimeString).split('T')[0].split(' ')[0] : null;
        };

        const allDates = [...new Set(data?.map(d => extractDate(d.submittedDateTime)).filter(Boolean))].sort();

        // If there's no data or no dates, return a safe object immediately
        if (!data || data.length === 0 || allDates.length === 0) {
            return { chartData: { labels: [], datasets: [] }, options: {} };
        }


        const studentScoresByDate = data?.reduce((acc, assignment) => {
            const studentName = assignment.student?.name;
            const dateOnly = extractDate(assignment.submittedDateTime);
            const numberOfQuestions = assignment.evaluation?.numberOfQuestions;

            if (!studentName || !dateOnly || typeof assignment.score !== 'number' || !numberOfQuestions || numberOfQuestions === 0) return acc;

            if (!acc[studentName]) {
                acc[studentName] = {};
            }

            acc[studentName][dateOnly] = ((assignment.score / numberOfQuestions) * 100);

            return acc;
        }, {});

        const datasets = Object.keys(studentColors).map(studentName => {
            const studentDataPoints = allDates.map(date => {
                // Use the nullish coalescing operator (??) to safely return null if the score is missing
                return studentScoresByDate?.[studentName]?.[date] ?? null;
            });

            const color = studentColors[studentName];

            return {
                label: studentName,
                data: studentDataPoints,
                borderColor: color,
                backgroundColor: color,
                spanGaps: true,
                pointRadius: 6,
                pointHoverRadius: 8,
            };
        });

        const finalChartData = {
            labels: allDates,
            datasets: datasets,
        };

        const finalOptions = {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                x: {
                    type: 'category',
                    title: {
                        display: true,
                        text: 'Submitted Date (Day Only)',
                    },
                    ticks: {
                        maxRotation: 45,
                        minRotation: 45,
                    },
                },
                y: {
                    min: 0,
                    max: 100,
                    title: {
                        display: true,
                        text: 'Score (%)',
                    },
                },
            },
            plugins: {
                legend: {
                    position: 'top',
                },
                tooltip: {
                    mode: 'index',
                    intersect: false,
                },
            }
        };

        // 🎯 FIX: Return object keys (chartData, options) must match destructured names
        return {
            chartData: finalChartData,
            options: finalOptions
        };

    }, [data, studentColors]);

    // Defensive check: If chartData is empty, return null or a message
    if (!chartData || chartData.labels.length === 0) return <div className="p-4 text-center text-gray-500">No score data available to generate a trend line.</div>;


    return (
        <Line data={chartData} options={options} />
    );
};
// --- START: Renamed function to Custom Hook ---
export function useChapterTrendChartAggregator(chapters, batches, assignmentMetrics) {
    const chapterCodeToNameMap = useMemo(() => {
        return (chapters || []).reduce((acc, c) => ({
            ...acc,
            [c.value]: c.label
        }), {});
    }, [chapters]);

    const batchIdToNameMap = useMemo(() => {
        return (batches || []).reduce((acc, b) => ({
            ...acc,
            [b.id]: b.batchName
        }), {});
    }, [batches]);

    const aggregateChapterTrends = useCallback(() => {

        const assignments = assignmentMetrics || [];

        const chapterBatchCountMap = assignments.reduce((acc, assignment) => {

            const chapterString = assignment.evaluation?.chapter;
            const batchId = assignment.batch?.id;

            if (!chapterString || !batchId) return acc;

            const chapterCodes = chapterString
                .split(',')
                .map(code => code.trim())
                .filter(code => code.length > 0);

            chapterCodes.forEach(chapterCode => {
                if (!acc[chapterCode]) {
                    acc[chapterCode] = {};
                }

                acc[chapterCode][batchId] = (acc[chapterCode][batchId] || 0) + 1;
            });

            return acc;
        }, {});

        const allBatchIds = Array.from(new Set(assignments.map(a => a.batch?.id).filter(Boolean)));
        const allChapterCodes = Object.keys(chapterBatchCountMap);
        const chartLabels = allChapterCodes.map(code => chapterCodeToNameMap[code] || `Code: ${code}`);

        const chartDatasets = allBatchIds.map((batchId, index) => ({
            label: batchIdToNameMap[batchId] || `Batch ID: ${batchId}`,
            backgroundColor: COLORS[index % COLORS.length],
            data: allChapterCodes.map(chapterCode => {
                return chapterBatchCountMap[chapterCode]?.[batchId] || 0;
            }),
        }));

        return { labels: chartLabels, datasets: chartDatasets };

    }, [assignmentMetrics, chapterCodeToNameMap, batchIdToNameMap]);
    return { aggregateChapterTrends, chapterCodeToNameMap, batchIdToNameMap };
}
// --- END: Renamed function to Custom Hook ---
// --- START: Renamed function to Custom Hook ---
export function useQuestionDistributionAggregator(assignmentMetrics) {
    return useCallback(() => {
        const metrics = assignmentMetrics || [];

        const difficultyCounts = metrics.reduce((acc, asignmt) => {
            const difficultyString = asignmt.evaluation?.difficulty;

            if (difficultyString) {
                const difficulties = difficultyString
                    .split(',')
                    .map(d => d.trim())
                    .filter(d => d.length > 0);

                difficulties.forEach(difficulty => {
                    acc[difficulty] = (acc[difficulty] || 0) + 1;
                });
            }
            return acc;

        }, {});

        const formattedData = Object.keys(difficultyCounts || {}).map(difficulty => ({
            label: difficulty,
            value: difficultyCounts[difficulty],
            color: COLORS[Object.keys(difficultyCounts).indexOf(difficulty) % COLORS.length]
        }));

        return formattedData;

    }, [assignmentMetrics]);
}
// --- END: Renamed function to Custom Hook ---
// --- START: Renamed function to Custom Hook ---
export function useStudentAssignmentStatusAggregator(students, assignmentMetrics) {
    const studentsMap = students || {};

    const studentIdToNameMap = useMemo(() => {
        const studentList = Array.isArray(studentsMap) ? studentsMap : Object.values(studentsMap);

        return (studentList || []).reduce((acc, s) => ({
            ...acc,
            [s.id]: s.name
        }), {});
    }, [studentsMap]);

    const aggregateStudentStatus = useCallback(() => {

        const assignments = assignmentMetrics || [];
        let allStudentIds = [];

        const studentStatusCountMap = assignments.reduce((acc, assignment) => {

            const studentId = assignment.student?.id;
            const status = assignment.status;

            if (!studentId || !status) return acc;

            if (!allStudentIds.includes(studentId)) {
                allStudentIds.push(studentId);
            }

            if (!acc[studentId]) {
                acc[studentId] = {};
            }

            acc[studentId][status] = (acc[studentId][status] || 0) + 1;

            return acc;
        }, {});

        const finalStudentIds = allStudentIds.filter(id => studentIdToNameMap[id]);

        const chartLabels = finalStudentIds.map(id => studentIdToNameMap[id] || `ID: ${id}`);

        const chartDatasets = STATUSES.map(status => ({
            label: status,
            backgroundColor: STATUS_COLORS[status] || '#cccccc',
            data: finalStudentIds.map(studentId => {
                return studentStatusCountMap[studentId]?.[status] || 0;
            }),
        }));

        return { labels: chartLabels, datasets: chartDatasets };

    }, [assignmentMetrics, studentIdToNameMap]);
    return { aggregateStudentStatus, studentIdToNameMap };
}
